import React, { useState } from 'react';
import { Badge, IconButton, Popover, List, ListItem, ListItemText, Typography, Box, Divider, Button } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useAviso } from '../../contexts/AvisoContext';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const AvisoNotificacao: React.FC = () => {
  const { avisos, avisosNaoLidos, marcarComoLido } = useAviso();
  const navigate = useNavigate();
  
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  const handleAvisoClick = async (id: string) => {
    try {
      // Marcar o aviso como lido e aguardar a conclusão
      await marcarComoLido(id);
      
      // Fechar o popover e navegar para a página de avisos
      handleClose();
      navigate('/avisos');
    } catch (error) {
      console.error('Erro ao marcar aviso como lido:', error);
    }
  };
  
  const handleVerTodos = () => {
    handleClose();
    navigate('/avisos');
  };
  
  // Filtrar apenas avisos não lidos para exibir no popover
  const avisosParaExibir = avisos
    .filter(aviso => !aviso.lido)
    .slice(0, 5); // Limitar a 5 avisos não lidos
  
  const open = Boolean(anchorEl);
  const id = open ? 'avisos-popover' : undefined;
  
  return (
    <>
      <IconButton
        color="inherit"
        aria-label="Notificações"
        onClick={handleClick}
        size="large"
        className="relative notification-bell"
      >
        <Badge 
          badgeContent={avisosNaoLidos} 
          color="error" 
          max={99}
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.75rem',
              fontWeight: 'bold',
              minWidth: '20px',
              height: '20px',
              padding: '0 4px',
            }
          }}
        >
          <NotificationsIcon 
            className="text-bravo-secondary hover:text-bravo-primary" 
            fontSize="medium"
          />
        </Badge>
      </IconButton>
      
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          '& .MuiPopover-paper': {
            width: '320px',
            maxHeight: '400px',
          }
        }}
      >
        <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
          <Typography variant="h6" component="div" sx={{ mb: 1 }}>
            Avisos
          </Typography>
          
          {avisosNaoLidos > 0 ? (
            <>
              <List sx={{ py: 0 }}>
                {avisosParaExibir.map((aviso) => (
                  <React.Fragment key={aviso.id}>
                    <ListItem 
                      alignItems="flex-start" 
                      sx={{ 
                        px: 1, 
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                      onClick={() => handleAvisoClick(aviso.id)}
                    >
                      <ListItemText
                        primary={
                          <Typography
                            variant="subtitle2"
                            color="text.primary"
                            sx={{ fontWeight: 'bold' }}
                          >
                            {aviso.titulo}
                          </Typography>
                        }
                        secondary={
                          <>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                mb: 0.5
                              }}
                            >
                              {aviso.conteudo}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              component="span"
                            >
                              {format(new Date(aviso.data_criacao), "dd/MM/yyyy 'às' HH:mm")}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))}
              </List>
              
              {avisosNaoLidos > avisosParaExibir.length && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                  + {avisosNaoLidos - avisosParaExibir.length} avisos não exibidos
                </Typography>
              )}
              
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button 
                  variant="contained" 
                  size="small" 
                  onClick={handleVerTodos}
                >
                  Ver todos os avisos
                </Button>
              </Box>
            </>
          ) : (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Não há avisos não lidos
              </Typography>
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default AvisoNotificacao;
